import { FormattedStreamValue } from '@squaredup/data-streams';
import { getTicks } from '@visx/scale';
import { ScaleBand, ScaleLinear } from '@visx/vendor/d3-scale';
import { tickCount } from 'dashboard-engine/util/tickFormatter';
import { NUM_Y_TICKS } from 'dashboard-engine/visualisations/getGridYValues';
import {
    barGraphDefaultBottomMargin,
    barGraphLabelMarginLeft,
    barGraphWithoutLabelMarginLeft,
    maxTickWidth
} from '../Config';
import { categoriesToTicks, ResolvedGroupedDataObject } from '../dataUtils';

const getCategoryAsString = (c: FormattedStreamValue) => {
    return typeof c.value === 'string' ? c.value : c.formatted;
};

/**
 * Returns a function that calculates the pixel width of a given value when rendered as text
 * This is used to determine the size of tick labels on the axes
 * Uses an OffscreenCanvas to measure text width without rendering it in the DOM
 * @returns A function that takes a value and returns its pixel width
 */
export const getTickSizeCalculator = () => {
    const canvas = new OffscreenCanvas(1, 1);
    const ctx = canvas.getContext('2d');

    return (value: string | number | null) => {
        if (!ctx) {
            return 0;
        }
        // Match the font used for ticks in the chart
        ctx.font = '11px Inter';
        const text = `${value}`;
        const metrics = ctx.measureText(text);
        return metrics.width;
    };
};

export const fitTickIntoWidth = (tick: string, width: number) => {
    const tickSizeCalculator = getTickSizeCalculator();

    if (tickSizeCalculator(tick) <= width) {
        return tick; // No need to truncate
    }

    let truncatedTick = tick + '..'; // Start with the original tick and add ellipsis
    while (truncatedTick.length > 2 && tickSizeCalculator(truncatedTick) > width) {
        truncatedTick = truncatedTick.slice(0, -3); // Remove last character including ellipsis
        truncatedTick = truncatedTick.trimEnd(); // Ensure we don't leave trailing spaces
        truncatedTick += '..';
    }

    return truncatedTick.length > 0 ? truncatedTick : '..';
};

/**
 * Calculates the x position and formatted label for ticks on the horizontal axis
 */
export const calculateHorizontalTicks = ({
    scale,
    graphWidth,
    formatter
}: {
    scale: ScaleLinear<number, number>;
    graphWidth: number;
    formatter: (value: unknown) => string;
}) => {
    const numTicks = tickCount(graphWidth, 50);
    const raw = getTicks(scale, numTicks).filter(Number.isInteger);

    const formatted = raw.map(formatter);

    return raw.reduce<{ tick: number | string; xPosition: number; key: string }[]>((acc, tick, index) => {
        const xPosition = scale(tick);
        const formattedTick = formatted[index];

        acc.push({
            tick: formattedTick,
            xPosition,
            key: `${tick}_${xPosition}_${index}`
        });

        return acc;
    }, []);
};

/**
 * Calculates the x position and formatted label for ticks on the vertical axis
 */
export const calculateVerticalTicks = ({
    series,
    isDateColumn,
    scale,
    graphWidth
}: {
    series: ResolvedGroupedDataObject[];
    isDateColumn: boolean;
    scale: ScaleBand<string>;
    graphWidth: number;
}) => {
    const barWidth = scale.bandwidth();

    const categoryGroupValues = series.map((d) => d.groupValue ?? '');

    const categories = isDateColumn
        ? categoriesToTicks(categoryGroupValues.map(getCategoryAsString))
        : categoryGroupValues.map(getCategoryAsString);

    const getXPosition = (category: string) => {
        return categories.length === 1 ? graphWidth / 2 : (scale(category) ?? 0) + barWidth / 2;
    };

    return series.reduce<{ tick: string; xPosition: number; key: string }[]>((positions, category, i) => {
        const xPosition = getXPosition(category.__group__ ?? '');

        // Skip ticks which are too close to the previous tick
        if (positions.length > 0 && xPosition - positions[positions.length - 1].xPosition < 20) {
            return positions;
        }

        positions.push({
            tick: categories[i],
            xPosition,
            key: `${categories[i]}_${xPosition}_${i}`
        });

        return positions;
    }, []);
};

/**
 * Calculates the margin needed based on whether ticks are rotated and the labels
 * @param rotateTicks Whether ticks are rotated
 * @param labels The tick labels
 * @returns The calculated margin
 */
export const calculateBottomMargin = (rotateTicks: boolean, labels: (string | number | null)[], showLabel: boolean) => {
    const maxMargin = showLabel ? barGraphDefaultBottomMargin : 30;

    // If labels aren't rotated, just use a default margin
    if (!rotateTicks) {
        return maxMargin;
    }

    const tickSizeCalculator = getTickSizeCalculator();

    // Get the longest label, and estimate it's pixel size
    const longestLabelSize = Math.min(maxTickWidth, Math.max(...labels.map(tickSizeCalculator)));

    // Add some additional space so it's not right against the label
    const margin = showLabel ? 30 : 10;

    // Apply a minimum margin in the case of small labels
    return Math.max(maxMargin, longestLabelSize + margin);
};

/**
 * Calculates the left margin needed for the Y-axis based on the value scale and tick formatter
 * @param valueScale The scale for the Y-axis values
 * @param yAxisTickFormatter Function to format the Y-axis tick values
 * @returns The calculated left margin in pixels
 */
export const calculateLeftMargin = (
    valueScale: ScaleLinear<number, number>,
    yAxisTickFormatter: (value: string | number | Date) => string,
    showLabel: boolean
) => {
    const tickSizeCalculator = getTickSizeCalculator();
    const raw = getTicks(valueScale, NUM_Y_TICKS);
    const yTicks = raw.map((tick) => yAxisTickFormatter(tick.valueOf()));

    const margin = showLabel ? barGraphLabelMarginLeft : barGraphWithoutLabelMarginLeft;
    return Math.max(...yTicks.map(tickSizeCalculator)) + margin;
};

export const calculateRightMargin = (labels: string[]) => {
    const tickSizeCalculator = getTickSizeCalculator();

    // Get the longest label in pixels
    const longestLabelSize = Math.min(maxTickWidth, Math.max(...labels.map(tickSizeCalculator)));

    // Add some additional space so it's not right against the label
    const margin = longestLabelSize + 20;

    // Apply a minimum margin in the case of small labels
    return Math.max(20, margin);
};

/**
 * Determine if ticks should be rotated based on actual tick positions
 * @param tickPositions The calculated tick positions
 * @param graphWidth The width of the graph
 * @param marginLeft The left margin of the graph
 * @returns Whether ticks should be rotated
 */
export const shouldRotateTicks = (
    tickPositions: {
        tick: number | string;
        xPosition: number;
        key: string;
    }[],
    graphWidth: number,
    marginLeft: number
) => {
    if (tickPositions.length <= 1) {
        return false;
    }

    const tickSizeCalculator = getTickSizeCalculator();

    const labelWidths = tickPositions.map((t) => Math.min(maxTickWidth, tickSizeCalculator(t.tick)));

    // Get the maximum label width
    const maxLabelWidth = Math.max(...labelWidths);

    // Calculate minimum distance between any two adjacent ticks
    let minDistance = Infinity;
    for (let i = 1; i < tickPositions.length; i++) {
        const distance = tickPositions[i].xPosition - tickPositions[i - 1].xPosition;
        minDistance = Math.min(minDistance, distance);
    }

    // Add some buffer for readability
    const buffer = 10;

    // If max label width (plus buffer) is greater than minimum distance, we need rotation
    const needsRotation = maxLabelWidth + buffer > minDistance;

    // Also check if the total width of all labels would exceed available space
    const totalLabelWidth = labelWidths.reduce((sum, w) => sum + w + buffer, 0);
    const availableWidth = graphWidth - marginLeft;
    const exceedsAvailableSpace = totalLabelWidth > availableWidth;

    return needsRotation || exceedsAvailableSpace;
};
